import {
	assembleStandaloneLiteralValueFromDataType,
	DefinitionEntityState,
	DynamicValueTypes,
	ExecutionTerminationType,
	getDeclaration,
	IDynamicValue,
	InternalCallState,
	resolveEntityName,
	toPascalCase
} from '@elyx-code/project-logic-tree';
import { Execution, IExecutionResult } from '.';
import { EditorService } from '../editor';
import { resolveValue } from './value-resolution';
import { popupNotification } from '../../popup-notification';
import { createQueryErrorMessage } from '../local-relational-database/utils';
import { IUpdateQuery } from 'jsstore';

export async function browserCreatePersistedEntityImplementation(
	entity: InternalCallState,
	inputs: IDynamicValue[],
	project: EditorService,
	execution?: Execution
): Promise<IExecutionResult> {
	const valueResolutionContext = execution?.valueResolutionContext || project;
	const variableValue = resolveValue(entity.parent, valueResolutionContext);

	if (!variableValue?.value?.value) {
		popupNotification({
			text: 'No value provided to save in the local testing database',
			color: 'error'
		});

		return {
			value:
				entity.catchesError || entity.errorCalls.length
					? ExecutionTerminationType.CaughtError
					: ExecutionTerminationType.UnhandledError,
			error: null,
			result: null,
			entity
		};
	}

	const persistedEntity: DefinitionEntityState = getDeclaration(entity.parent)
		?.dataType?.entity as DefinitionEntityState;

	const databaseClient =
		project.localDatabaseStore?.getDatabaseForPersistedEntity(
			persistedEntity
		);

	const tableName = toPascalCase(
		resolveEntityName(persistedEntity, project.logic)
	);

	// Save the value in the local database
	const tableDef = databaseClient.getTable(persistedEntity.id);

	const selectedTableName = tableDef.tableName;

	const insertQueryRequest = {
		into: selectedTableName,
		values: [variableValue.value.value]
	};

	try {
		await databaseClient.insert(insertQueryRequest);
	} catch (error) {
		const message = createQueryErrorMessage(error, [
			{
				name: tableName,
				entity: persistedEntity
			}
		]);

		popupNotification({
			text: message,
			color: 'error'
		});

		let resolvedErrorValue: IDynamicValue | null = null;

		// TODO: Add error to entity if not present
		if (!!entity.error) {
			const resolvedDataType = entity.error?.getDataType(null);
			const literalValueInstance =
				assembleStandaloneLiteralValueFromDataType(
					resolvedDataType,
					entity.error,
					project.logic
				);

			if (!!resolvedDataType?.asType) {
				literalValueInstance?.setValueAsTypeSingle(message as any);
			} else {
				literalValueInstance?.metaSync(
					{
						value: message
					},
					null
				);
			}

			resolvedErrorValue = {
				valueOwner: entity.error,
				type: DynamicValueTypes.ExecutionResult,
				inheritanceLink: null,
				value: literalValueInstance
			};
		}

		return {
			value:
				entity.catchesError || entity.errorCalls.length
					? ExecutionTerminationType.CaughtError
					: ExecutionTerminationType.UnhandledError,
			entity: entity,
			error: resolvedErrorValue,
			result: null
		};
	}

	const primaryKeyName = tableDef.columns.find(
		(column) => column.primaryKey
	)?.columnName;

	const rowPrimaryKey = (
		variableValue?.value?.value as {
			[primaryKeyName: string]: any;
		}
	)[primaryKeyName];

	popupNotification({
		text: `New value (${primaryKeyName}: ${rowPrimaryKey}) saved in the local testing database for table "${tableName}"`,
		color: 'primary',
		delay: 5000
	});

	return {
		value: ExecutionTerminationType.Success,
		error: null,
		result: null,
		entity
	};
}

export async function browserDeletePersistedEntityImplementation(
	entity: InternalCallState,
	inputs: IDynamicValue[],
	project: EditorService,
	execution?: Execution
): Promise<IExecutionResult> {
	const valueResolutionContext = execution?.valueResolutionContext || project;
	const variableValue = resolveValue(entity.parent, valueResolutionContext);

	if (!variableValue?.value?.value) {
		popupNotification({
			text: 'No value provided to delete from the local testing database',
			color: 'error'
		});

		return {
			value:
				entity.catchesError || entity.errorCalls.length
					? ExecutionTerminationType.CaughtError
					: ExecutionTerminationType.UnhandledError,
			error: null,
			result: null,
			entity
		};
	}

	// Remove the value from the local database
	const persistedEntity: DefinitionEntityState = getDeclaration(entity.parent)
		?.dataType?.entity as DefinitionEntityState;

	const databaseClient =
		project.localDatabaseStore?.getDatabaseForPersistedEntity(
			persistedEntity
		);

	const tableName = toPascalCase(
		resolveEntityName(persistedEntity, project.logic)
	);

	// Save the value in the local database
	// Convert table entity id (resolvedSelectedTable) to table name
	const tableDef = databaseClient.getTable(persistedEntity.id);

	const selectedTableName = tableDef.tableName;

	const primaryKeyName = tableDef.columns.find(
		(column) => column.primaryKey
	)?.columnName;

	const rowPrimaryKey = (
		variableValue?.value?.value as {
			[primaryKeyName: string]: any;
		}
	)[primaryKeyName];

	const deleteQueryRequest = {
		from: selectedTableName,
		where: {
			[primaryKeyName]: rowPrimaryKey
		}
	};

	try {
		await databaseClient.remove(deleteQueryRequest);
	} catch (error) {
		const message = createQueryErrorMessage(error, [
			{
				name: tableName,
				entity: persistedEntity
			}
		]);

		popupNotification({
			text: message,
			color: 'error'
		});

		let resolvedErrorValue: IDynamicValue | null = null;

		// TODO: Add error to entity if not present
		if (!!entity.error) {
			const resolvedDataType = entity.error?.getDataType(null);
			const literalValueInstance =
				assembleStandaloneLiteralValueFromDataType(
					resolvedDataType,
					entity.error,
					project.logic
				);

			if (!!resolvedDataType?.asType) {
				literalValueInstance?.setValueAsTypeSingle(message as any);
			} else {
				literalValueInstance?.metaSync(
					{
						value: message
					},
					null
				);
			}

			resolvedErrorValue = {
				valueOwner: entity.error,
				type: DynamicValueTypes.ExecutionResult,
				inheritanceLink: null,
				value: literalValueInstance
			};
		}

		return {
			value:
				entity.catchesError || entity.errorCalls.length
					? ExecutionTerminationType.CaughtError
					: ExecutionTerminationType.UnhandledError,
			entity: entity,
			error: resolvedErrorValue,
			result: null
		};
	}

	popupNotification({
		text: `Value (${primaryKeyName}: ${rowPrimaryKey}) deleted from the the local testing database for table "${tableName}"`,
		color: 'primary',
		delay: 5000
	});

	return {
		value: ExecutionTerminationType.Success,
		error: null,
		result: null,
		entity
	};
}

export async function browserUpdatePersistedEntityImplementation(
	entity: InternalCallState,
	inputs: IDynamicValue[],
	project: EditorService,
	execution?: Execution
): Promise<IExecutionResult> {
	const valueResolutionContext = execution?.valueResolutionContext || project;
	const variableValue = resolveValue(entity.parent, valueResolutionContext);

	if (!variableValue?.value?.value) {
		popupNotification({
			text: 'No value provided to update from the local testing database',
			color: 'error'
		});

		return {
			value:
				entity.catchesError || entity.errorCalls.length
					? ExecutionTerminationType.CaughtError
					: ExecutionTerminationType.UnhandledError,
			error: null,
			result: null,
			entity
		};
	}

	// Remove the value from the local database
	const persistedEntity: DefinitionEntityState = getDeclaration(entity.parent)
		?.dataType?.entity as DefinitionEntityState;

	const databaseClient =
		project.localDatabaseStore?.getDatabaseForPersistedEntity(
			persistedEntity
		);

	const tableName = toPascalCase(
		resolveEntityName(persistedEntity, project.logic)
	);

	// Save the value in the local database
	// Convert table entity id (resolvedSelectedTable) to table name
	const tableDef = databaseClient.getTable(persistedEntity.id);

	const selectedTableName = tableDef.tableName;

	const primaryKeyName = tableDef.columns.find(
		(column) => column.primaryKey
	)?.columnName;

	const rowPrimaryKey = (
		variableValue?.value?.value as {
			[primaryKeyName: string]: any;
		}
	)[primaryKeyName];

	const sanitizedValueToUpdate = {
		...(variableValue.value.value as {
			[primaryKeyName: string]: any;
		})
	};
	delete sanitizedValueToUpdate[primaryKeyName];

	const updateQueryRequest: IUpdateQuery = {
		in: selectedTableName,
		where: {
			[primaryKeyName]: rowPrimaryKey
		},
		set: sanitizedValueToUpdate as {
			[columnName: string]: any;
		}
	};

	try {
		await databaseClient.update(updateQueryRequest);
	} catch (error) {
		const message = createQueryErrorMessage(error, [
			{
				name: tableName,
				entity: persistedEntity
			}
		]);

		popupNotification({
			text: message,
			color: 'error'
		});

		let resolvedErrorValue: IDynamicValue | null = null;

		// TODO: Add error to entity if not present
		if (!!entity.error) {
			const resolvedDataType = entity.error?.getDataType(null);
			const literalValueInstance =
				assembleStandaloneLiteralValueFromDataType(
					resolvedDataType,
					entity.error,
					project.logic
				);

			if (!!resolvedDataType?.asType) {
				literalValueInstance?.setValueAsTypeSingle(message as any);
			} else {
				literalValueInstance?.metaSync(
					{
						value: message
					},
					null
				);
			}

			resolvedErrorValue = {
				valueOwner: entity.error,
				type: DynamicValueTypes.ExecutionResult,
				inheritanceLink: null,
				value: literalValueInstance
			};
		}

		return {
			value:
				entity.catchesError || entity.errorCalls.length
					? ExecutionTerminationType.CaughtError
					: ExecutionTerminationType.UnhandledError,
			entity: entity,
			error: resolvedErrorValue,
			result: null
		};
	}
	popupNotification({
		text: `Value (${primaryKeyName}: ${rowPrimaryKey}) updated in the local testing database for table "${tableName}"`,
		color: 'primary',
		delay: 5000
	});

	return {
		value: ExecutionTerminationType.Success,
		error: null,
		result: null,
		entity
	};
}
